import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { computeFlags, isEarlyExit, shiftMinutes } from '../../domain/flags';
import { compressImage } from '../../lib/compressImage';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';
import { haversineMeters } from '../../domain/distance';
import { stampPhoto } from '../../domain/photoStamp';
import { useAuth } from '../../components/AuthProvider';
import CampoFoto from '../../components/CampoFoto';
import { getMyShiftById, type MyShiftRow } from '../../api/assignments';
import { setPdvLocation } from '../../api/pdv';
import { listFormFieldsDelTurno } from '../../api/campaigns';
import {
  getEntryDeviceTs,
  submitAttendance,
  uploadAttendancePhoto,
  uploadFormFieldPhoto,
} from '../../api/attendance';
import { enqueue, listPending, type PendingAttendance } from '../../offline/queue';
import { submitOrQueue } from '../../lib/submitOrQueue';
import { fetchServerNowIso } from '../../lib/serverTime';
import { loadSettings } from '../../lib/settings';
import type { FormFieldRow } from '../../lib/db-types';

const LIMA_TZ = 'America/Lima';
/** 25 s: un iPhone con GPS frío dentro de una tienda tarda 15-30 s en fijar posición.
 *  Con 8 s (el valor original) fallaba casi siempre en campo. */
const GPS_TIMEOUT_MS = 25000;
/** Margen sobre `GPS_TIMEOUT_MS` para el reloj propio de `getGpsFix`. El `timeout` del
 *  navegador no siempre dispara; este sí. Ver el comentario grande dentro de la función. */
const GPS_GRACIA_MS = 5000;
/** Acepta una posición de hasta 2 minutos: para una geocerca de cientos de metros es
 *  de sobra, y evita esperar un fix nuevo cuando el teléfono ya sabe dónde está. */
const GPS_MAX_AGE_MS = 120000;

export interface GpsFix {
  lat: number;
  lng: number;
  accuracy: number;
}

/** Por qué no se pudo obtener la ubicación. Se usa para decirle al usuario qué hacer:
 *  no es lo mismo "no diste permiso" que "no hay señal". */
export type GpsErrorKind = 'sin_soporte' | 'permiso_denegado' | 'sin_senal' | 'tiempo_agotado';

export interface GpsResult {
  fix: GpsFix | null;
  error: GpsErrorKind | null;
}

/** Mensaje accionable por tipo de fallo. En iOS el permiso denegado NO se vuelve a
 *  preguntar: hay que reactivarlo en Ajustes, así que se indica la ruta exacta. */
export function gpsErrorMessage(kind: GpsErrorKind): string {
  switch (kind) {
    case 'sin_soporte':
      return 'Este navegador no permite compartir la ubicación. Puedes marcar igual: quedará como "Sin GPS".';
    case 'permiso_denegado':
      return 'No diste permiso de ubicación. En iPhone: Ajustes → Privacidad y seguridad → Localización → Safari → "Al usar la app", y vuelve a intentar. En Android: toca el candado de la barra de direcciones → Permisos → Ubicación.';
    case 'sin_senal':
      return 'Tu teléfono no logró ubicarte. Sal un momento de la zona techada o acércate a una ventana y toca "Reintentar ubicación".';
    case 'tiempo_agotado':
      return 'La ubicación tardó demasiado. Toca "Reintentar ubicación": dentro de tiendas el GPS puede demorar hasta medio minuto.';
  }
}

/** Posición actual. Nunca rechaza: devuelve el motivo del fallo para poder explicarlo.
 *  IMPORTANTE: llamar desde un gesto del usuario (un toque). En iOS Safari, pedir la
 *  ubicación al cargar la pantalla hace que el aviso de permiso no aparezca de forma
 *  fiable, y el marcado quedaba siempre "Sin GPS" sin que el recurso pudiera hacer nada. */
export function getGpsFix(timeoutMs: number = GPS_TIMEOUT_MS): Promise<GpsResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ fix: null, error: 'sin_soporte' });
      return;
    }
    // 🔴 TEMPORIZADOR PROPIO. NO ALCANZA CON EL `timeout` DE `getCurrentPosition`.
    //
    // El 14-set dos impulsadoras no pudieron registrarse: apoyaban el dedo en el botón de la foto
    // y **no pasaba nada**, «como si el botón estuviera dibujado nada más». Esa es exactamente la
    // pinta que tiene un `<input type="file" disabled>`, y la foto del marcado está deshabilitada
    // hasta que el GPS conteste.
    //
    // El `timeout` que recibe `getCurrentPosition` **no es una garantía**: si el aviso de permiso
    // queda abierto sin responder, o si se descarta tocando afuera, o si la página corre dentro
    // del navegador de otra app con la ubicación bloqueada, hay navegadores que **no llaman a
    // ningún callback, nunca**. Sin este reloj la promesa no se resuelve jamás, `gpsReady` se
    // queda en falso para siempre, el cartel sigue diciendo «Obteniendo ubicación…» —que promete
    // «unos segundos»— y el botón de la foto queda muerto el resto del día.
    //
    // Sin GPS se puede marcar igual: queda con la bandera «Sin GPS» y el coordinador lo revisa.
    // O sea que colgarse esperando no protege ningún dato: solo impide trabajar.
    let resuelto = false;
    const terminar = (r: GpsResult) => {
      if (resuelto) return;
      resuelto = true;
      clearTimeout(reloj);
      resolve(r);
    };
    const reloj = setTimeout(
      () => terminar({ fix: null, error: 'tiempo_agotado' }),
      timeoutMs + GPS_GRACIA_MS,
    );

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        terminar({
          fix: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
          error: null,
        }),
      (err) => {
        const kind: GpsErrorKind =
          err.code === err.PERMISSION_DENIED
            ? 'permiso_denegado'
            : err.code === err.TIMEOUT
              ? 'tiempo_agotado'
              : 'sin_senal';
        terminar({ fix: null, error: kind });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: GPS_MAX_AGE_MS },
    );
  });
}

/**
 * Segunda línea del sello: "lat,lng ±Xm", "Sin GPS" o "Ubicación pendiente".
 *
 * ⚠️ POR QUÉ SON TRES Y NO DOS. Desde el 14-set la foto ya no espera al GPS, así que puede
 * tomarse ANTES de que la ubicación conteste —y el marcado igual puede terminar CON coordenadas,
 * porque se leen al enviar—. Si los dos casos se sellaran igual, una foto que dice "Sin GPS" al
 * lado de una fila que SÍ tiene coordenadas parecería una contradicción, y quien valida no
 * tendría cómo saber a cuál creerle.
 *
 * "Sin GPS" = se intentó y no se pudo. "Ubicación pendiente" = todavía no había contestado
 * cuando se tomó la foto.
 */
function latLngText(fix: GpsFix | null, resuelto: boolean = true): string {
  if (!fix) return resuelto ? 'Sin GPS' : 'Ubicación pendiente';
  return `${fix.lat.toFixed(5)},${fix.lng.toFixed(5)} ±${Math.round(fix.accuracy)}m`;
}

/** "5 h 30 min", "9 h", "40 min". En minutos redondos: nadie discute los segundos. */
function textoDuracion(minutos: number): string {
  const total = Math.round(minutos);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

export interface AvisoSalidaTempranaInput {
  /** Fecha de la posición (YYYY-MM-DD). */
  positionDate: string;
  /** Inicio y fin PROGRAMADOS del turno ('HH:mm' o 'HH:mm:ss'). */
  startTime: string;
  endTime: string;
  /** Ingreso ya marcado de ESTE turno; null cuando no se pudo leer. */
  entryDeviceTs: string | null;
  /** Hora que va a quedar registrada como salida (ISO). */
  ahora: string;
  /** `app_settings.half_day_threshold_pct` (hoy 75). */
  umbralPct: number;
}

export interface AvisoSalidaTemprana {
  avisar: boolean;
  mensaje: string;
}

/**
 * Decide si hay que avisarle a la persona —ANTES de registrar— que su salida va a
 * contar como medio día, y con qué palabras. Lo pidió el usuario así: *"cuando marquen
 * antes de tiempo, debería salir una alerta de salida antes de tiempo: solo se
 * considerará medio día de trabajo"*.
 *
 * El mensaje dice la hora REAL de fin de turno y la hora que va a quedar registrada.
 * Un texto genérico ("saliste antes") no le sirve para decidir: con las dos horas a la
 * vista puede esperar 20 minutos y cobrar el día entero, o marcar igual sabiendo lo que
 * le cuesta. Enterarse el día del pago es lo único inaceptable.
 *
 * La DECISIÓN es `isEarlyExit` (`domain/flags.ts`), la misma regla que aplica el
 * servidor: si esta pantalla decidiera por su cuenta, podría avisar de un medio día que
 * no ocurre —o callarse uno que sí—, y la persona dejaría de creerle al aviso.
 */
/**
 * El primer campo obligatorio que falta, o `null` si están todos. Función pura.
 *
 * Existe para que la MISMA comprobación sirva en los tres lugares donde hace falta: deshabilitar
 * el botón, avisar al tocarlo y frenar el envío. Antes solo estaba dentro del envío, así que el
 * botón invitaba a registrar y el error aparecía al final del camino.
 *
 * Es lo que hace que "sin responder no se marca salida" sea cierto EN LA PANTALLA. No es un
 * cerrojo del servidor y es una decisión, no un descuido: `attendance` y `form_responses` se
 * insertan en dos statements separados, así que ningún disparador puede ver la respuesta, y
 * ponerla como condición del INSERT obligaría a rechazar salidas — incluidas las que se
 * encolaron sin señal, que la cola daría por perdidas tras 5 intentos. Un cerrojo que puede
 * borrar la salida de alguien es peor que el problema que resuelve. La app exige, y el panel
 * delata a quien haya marcado sin responder.
 */
export function campoObligatorioIncompleto(
  fields: FormFieldRow[],
  values: Record<string, string>,
  fieldPhotos: Record<string, Blob>,
): string | null {
  for (const f of fields) {
    if (!f.required) continue;
    if (f.kind === 'foto') {
      if (!fieldPhotos[f.id]) return `Falta la foto de "${f.label}".`;
    } else if (!(values[f.id] ?? '').trim()) {
      return `Completa "${f.label}".`;
    }
  }
  return null;
}

export function avisoSalidaTemprana(i: AvisoSalidaTempranaInput): AvisoSalidaTemprana {
  const temprana = isEarlyExit({
    positionDate: i.positionDate,
    startTime: i.startTime,
    endTime: i.endTime,
    entryDeviceTs: i.entryDeviceTs,
    exitDeviceTs: i.ahora,
    halfDayThresholdPct: i.umbralPct,
  });
  if (!temprana || !i.entryDeviceTs) return { avisar: false, mensaje: '' };

  const trabajadoMin =
    (new Date(i.ahora).getTime() - new Date(i.entryDeviceTs).getTime()) / 60_000;
  const turnoMin = shiftMinutes(i.positionDate, i.startTime, i.endTime);
  const fin = i.endTime.slice(0, 5);
  const hora = formatInTimeZone(new Date(i.ahora), LIMA_TZ, 'HH:mm');

  return {
    avisar: true,
    mensaje:
      `Tu turno termina a las ${fin} y son las ${hora}: llevas ${textoDuracion(trabajadoMin)} ` +
      `de las ${textoDuracion(turnoMin)} que dura tu turno. Si registras la salida ahora, ` +
      'este día contará como medio día de trabajo (0.5) para el pago.',
  };
}

/** Aviso de confirmación antes de pisar la coordenada guardada de la tienda. */
export function textoConfirmacionUbicacion(distanciaM: number | null): string {
  if (distanciaM === null) {
    return 'Esta tienda todavía no tiene ubicación guardada. ¿Fijarla donde estás ahora?';
  }
  return `La ubicación guardada está a ${Math.round(distanciaM)} m de donde estás. ¿Actualizarla?`;
}

export default function Marcar() {
  const [params] = useSearchParams();
  const { roles } = useAuth();
  const kind: 'ingreso' | 'salida' = params.get('kind') === 'salida' ? 'salida' : 'ingreso';
  // Cuál de los turnos del día se está marcando. Lo pone MiTurno en el enlace del botón:
  // re-resolver "el turno de hoy" aquí sería ambiguo cuando el recurso tiene dos.
  const assignmentId = params.get('assignment') ?? '';

  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState<MyShiftRow | null>(null);
  const [fields, setFields] = useState<FormFieldRow[]>([]);
  const [gps, setGps] = useState<GpsFix | null>(null);
  const [gpsReady, setGpsReady] = useState(false);
  const [gpsError, setGpsError] = useState<GpsErrorKind | null>(null);
  /** Aviso cuando la foto va sin sello. No es un error: el marcado sigue siendo válido. */
  const [avisoFoto, setAvisoFoto] = useState<string | null>(null);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldPhotos, setFieldPhotos] = useState<Record<string, Blob>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<'sent' | 'queued' | null>(null);
  // Fijar la ubicación de la tienda (solo coordinador/admin): confirmación + resultado
  const [confirmarUbic, setConfirmarUbic] = useState(false);
  const [guardandoUbic, setGuardandoUbic] = useState(false);
  const [ubicFijada, setUbicFijada] = useState(false);
  // Regla del medio día: para avisar "salida antes de tiempo" hacen falta la hora del
  // ingreso de ESTE mismo turno y el umbral configurado. Si no se pueden leer, no se
  // avisa nada y se marca igual: el servidor recalcula la bandera de todos modos.
  const [entryTs, setEntryTs] = useState<string | null>(null);
  const [umbralMedioDia, setUmbralMedioDia] = useState<number | null>(null);
  // Confirmación de la salida temprana: se pide UNA vez, al tocar "Registrar".
  const [confirmarSalida, setConfirmarSalida] = useState(false);

  // 1) El turno indicado en la URL + campos extra del momento que se está marcando
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        if (!assignmentId) {
          if (alive) setShift(null);
          return;
        }
        const row = await getMyShiftById(assignmentId);
        if (!alive) return;
        setShift(row);
        if (row) {
          const all = await listFormFieldsDelTurno(
            row.position.campaign_id, row.position.pdv.id, row.position.date,
          );
          if (!alive) return;
          setFields(
            all
              .filter((f) => f.moment === kind || f.moment === 'ambos')
              .sort((a, b) => a.sort - b.sort),
          );
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'No se pudo cargar el turno.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [assignmentId, kind]);

  // 1b) Solo al marcar SALIDA: umbral del medio día y hora del ingreso de este turno.
  // El ingreso puede estar en el servidor o todavía en la cola local (se marcó sin
  // señal); se buscan los dos, en ese orden.
  useEffect(() => {
    if (kind !== 'salida' || !assignmentId) return;
    let alive = true;
    void (async () => {
      const settings = await loadSettings().catch(() => null);
      if (alive && settings) setUmbralMedioDia(settings.halfDayThresholdPct);
      let ts: string | null = null;
      try {
        ts = await getEntryDeviceTs(assignmentId);
      } catch {
        ts = null;
      }
      if (ts === null) {
        try {
          const pendientes = await listPending();
          ts =
            pendientes.find((p) => p.assignmentId === assignmentId && p.kind === 'ingreso')
              ?.deviceTs ?? null;
        } catch {
          ts = null;
        }
      }
      if (alive) setEntryTs(ts);
    })();
    return () => {
      alive = false;
    };
  }, [assignmentId, kind]);

  // 2) GPS: se pide UNA VEZ al abrir y luego solo a pedido del usuario.
  // El intento automático sirve cuando el permiso ya está concedido (caso normal del
  // día a día). Si falla, NO se reintenta solo: iOS solo muestra el aviso de permiso
  // de forma fiable desde un gesto, así que aparece el botón "Reintentar ubicación".
  const pedirUbicacion = useCallback(async () => {
    setGpsReady(false);
    const { fix, error } = await getGpsFix();
    setGps(fix);
    setGpsError(error);
    setGpsReady(true);
  }, []);

  useEffect(() => {
    let alive = true;
    void getGpsFix().then(({ fix, error }) => {
      if (!alive) return;
      setGps(fix);
      setGpsError(error);
      setGpsReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    },
    [photoUrl],
  );

  // 3) Foto principal: se sella ANTES de mostrarse (lo que se ve es lo que se sube)
  const onPhotoPicked = useCallback(
    async (file: File) => {
      if (!shift) return;
      setBusyPhoto(true);
      setError(null);
      const now = new Date();
      let lista: Blob | null = null;
      let conSello = true;
      try {
        lista = await stampPhoto(file, {
          dateTimeText: formatInTimeZone(now, LIMA_TZ, 'dd/MM/yyyy HH:mm'),
          latLngText: latLngText(gps, gpsReady),
          pdvName: shift.position.pdv.name,
        });
      } catch {
        /**
         * 🔴 EL SELLO ES EVIDENCIA ÚTIL, PERO LA EVIDENCIA ES LA FOTO.
         *
         * Si el sellado falla, antes se contestaba «vuelve a tomarla» y no había forma de marcar.
         * En un celular donde el sellado NO puede funcionar —Safari viejo, memoria justa— eso es
         * pedirle a alguien que repita para siempre algo que nunca va a andar: el 14-set dejó a
         * una impulsadora sin marcar ingreso toda la jornada.
         *
         * La fecha, la hora y las coordenadas se guardan igual en la fila del marcado; el sello
         * solo las repite ENCIMA de la imagen. Perder esa repetición es infinitamente más barato
         * que perder la jornada.
         */
        conSello = false;
        try {
          lista = await compressImage(file);
        } catch {
          lista = null;
        }
      }

      if (!lista) {
        setError('No se pudo procesar la foto. Vuelve a tomarla.');
        setBusyPhoto(false);
        return;
      }

      /**
       * Lo que se SUBE tiene que entrar en el bucket (tipo y tope de la 0314). Se mide el blob ya
       * procesado y no el original a propósito: el sellado lo deja en un JPEG de ~350 KB, así que
       * una cámara que saca fotos de 15 MB sigue marcando sin problema. Solo se rechaza cuando el
       * sellado y la compresión fallaron y lo que viajaría es el original tal cual. Rechazarlo acá,
       * y no en el servidor, es lo que evita que la marca se quede «fallida» en la cola sin señal.
       */
      const motivo = rechazoDeArchivo(lista);
      if (motivo !== null) {
        setError(motivo);
        setBusyPhoto(false);
        return;
      }

      const finalBlob = lista;
      setPhoto(finalBlob);
      setCapturedAt(now.toISOString());
      setPhotoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(finalBlob);
      });
      setAvisoFoto(conSello
        ? null
        : 'Tu foto se envía sin el sello de fecha y ubicación: este celular no pudo generarlo. '
          + 'El marcado queda igual de válido y tu coordinador lo ve normal.');
      setBusyPhoto(false);
    },
    [gps, gpsReady, shift],
  );

  // 4) Foto de un campo extra: también sellada, para que sirva como evidencia
  const onFieldPhotoPicked = useCallback(
    async (field: FormFieldRow, file: File) => {
      if (!shift) return;
      try {
        const stamped = await stampPhoto(file, {
          dateTimeText: formatInTimeZone(new Date(), LIMA_TZ, 'dd/MM/yyyy HH:mm'),
          latLngText: latLngText(gps, gpsReady),
          pdvName: shift.position.pdv.name,
        });
        setFieldPhotos((prev) => ({ ...prev, [field.id]: stamped }));
      } catch {
        setError(`No se pudo procesar la foto de "${field.label}".`);
      }
    },
    [gps, gpsReady, shift],
  );

  // 5) Registrar: flags + intento directo, con caída a la cola local
  const onSubmit = useCallback(async () => {
    if (!shift || !photo || !capturedAt) return;
    setSaving(true);
    setError(null);
    try {
      // Última barrera, con la MISMA regla que deshabilita el botón y la que corre al tocarlo.
      // Se conserva aunque `onRegistrar` ya haya comprobado: `onSubmit` también se alcanza desde
      // la confirmación de salida temprana, y una segunda puerta no cuesta nada.
      const falta = campoObligatorioIncompleto(fields, values, fieldPhotos);
      if (falta !== null) {
        setError(falta);
        setSaving(false);
        return;
      }

      const settings = await loadSettings();
      const serverTs = await fetchServerNowIso();
      // Flags de FEEDBACK VISUAL: viajan en el insert, pero el trigger
      // `before insert on attendance` (Grupo A) los recalcula en el servidor con
      // positions/pdv/app_settings y sobrescribe la columna. Manda el servidor.
      const flags = computeFlags({
        kind,
        deviceTs: capturedAt,
        serverTs,
        positionDate: shift.position.date,
        startTime: shift.position.start_time,
        endTime: shift.position.end_time,
        lat: gps?.lat ?? null,
        lng: gps?.lng ?? null,
        pdvLat: shift.position.pdv.lat,
        pdvLng: shift.position.pdv.lng,
        lateToleranceMin: settings.lateToleranceMin,
        pdvRadiusM: shift.position.pdv.radius_m, // radio de ESA tienda
        clockSkewMin: settings.clockSkewMin,
        entryDeviceTs: entryTs,
        halfDayThresholdPct: settings.halfDayThresholdPct,
      });

      const pending: PendingAttendance = {
        clientUuid: crypto.randomUUID(),
        assignmentId: shift.id,
        kind,
        deviceTs: capturedAt,
        lat: gps?.lat ?? null,
        lng: gps?.lng ?? null,
        accuracy: gps?.accuracy ?? null,
        photoBlob: photo,
        formResponses: fields
          .filter((f) => f.kind !== 'foto')
          .map((f) => ({ fieldId: f.id, value: (values[f.id] ?? '').trim() }))
          .filter((r) => r.value !== ''),
        flags,
        formPhotos: Object.entries(fieldPhotos).map(([fieldId, blob]) => ({ fieldId, blob })),
      };

      const result = await submitOrQueue(pending, {
        uploadPhoto: uploadAttendancePhoto,
        uploadFormPhoto: uploadFormFieldPhoto,
        submit: submitAttendance,
        enqueue,
      });
      setDone(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar el marcado.');
    } finally {
      setSaving(false);
    }
  }, [capturedAt, entryTs, fieldPhotos, fields, gps, kind, photo, shift, values]);

  // 6) Fijar la ubicación REAL de la tienda (solo coordinador/admin).
  // Las coordenadas de carga son aproximadas y disparan alertas falsas de "lejos del PDV".
  // Se REUTILIZA la ubicación que la pantalla ya obtuvo: no se vuelve a pedir el GPS.
  const puedeFijarUbicacion = roles.includes('coordinador') || roles.includes('admin');
  const pdvLat = shift?.position.pdv.lat ?? null;
  const pdvLng = shift?.position.pdv.lng ?? null;
  const distanciaGuardada =
    gps && pdvLat !== null && pdvLng !== null
      ? haversineMeters(gps.lat, gps.lng, pdvLat, pdvLng)
      : null;

  const fijarUbicacionPdv = useCallback(async () => {
    if (!shift || !gps) return;
    setGuardandoUbic(true);
    setError(null);
    try {
      await setPdvLocation(shift.position.pdv.id, gps.lat, gps.lng, gps.accuracy);
      // La pantalla ya tenía cargada la coordenada vieja: refrescarla en memoria evita
      // que el marcado que se está haciendo ahora mismo salga con "lejos del PDV".
      setShift((prev) =>
        prev
          ? {
              ...prev,
              position: {
                ...prev.position,
                pdv: {
                  ...prev.position.pdv,
                  lat: gps.lat,
                  lng: gps.lng,
                  location_source: 'terreno',
                  location_set_at: new Date().toISOString(),
                },
              },
            }
          : prev,
      );
      setUbicFijada(true);
      setConfirmarUbic(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la ubicación de la tienda.');
    } finally {
      setGuardandoUbic(false);
    }
  }, [gps, shift]);

  const titulo = kind === 'ingreso' ? 'Marcar ingreso' : 'Marcar salida';

  // Lo que falta responder, calculado en cada pintado sobre lo que ya está en memoria. Es la
  // única fuente para el aviso, para el botón deshabilitado y para el envío.
  const faltaObligatorio = campoObligatorioIncompleto(fields, values, fieldPhotos);

  // Alerta de salida antes de tiempo. Antes de la foto se evalúa con la hora actual
  // ("si sales ahora…"); tomada la foto, con la hora que va a quedar registrada.
  const aviso: AvisoSalidaTemprana =
    kind === 'salida' && shift !== null && umbralMedioDia !== null
      ? avisoSalidaTemprana({
          positionDate: shift.position.date,
          startTime: shift.position.start_time,
          endTime: shift.position.end_time,
          entryDeviceTs: entryTs,
          ahora: capturedAt ?? new Date().toISOString(),
          umbralPct: umbralMedioDia,
        })
      : { avisar: false, mensaje: '' };

  // "Registrar" con salida temprana no registra: pregunta. La persona decide con las
  // horas a la vista, y si dice que sí se marca igual — avisar no puede volverse un
  // impedimento: puede tener permiso, cita médica o una emergencia.
  const onRegistrar = useCallback(() => {
    // Los campos obligatorios se comprueban ANTES que la confirmación de salida temprana.
    // El orden importa y estaba al revés: al tocar "Registrar" salía primero el cartel de medio
    // día, se confirmaba, y recién entonces aparecía "Completa …". Quien pasaba por ahí ya había
    // decidido irse antes de tiempo, leído un aviso sobre su sueldo y confirmado — para
    // descubrir que además le faltaba un dato. Preguntar lo que falta primero es una pregunta;
    // preguntarlo después de todo eso es una trampa.
    if (faltaObligatorio !== null) {
      setError(faltaObligatorio);
      setConfirmarSalida(false);
      return;
    }
    if (aviso.avisar && !confirmarSalida) {
      setConfirmarSalida(true);
      return;
    }
    void onSubmit();
  }, [aviso.avisar, confirmarSalida, onSubmit, faltaObligatorio]);

  if (loading) return <p className="p-4 text-center text-gray-500">Cargando…</p>;

  if (done) {
    return (
      <div className="space-y-4 text-center">
        <div
          className={`rounded-xl p-4 ${done === 'sent' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'}`}
        >
          <p className="text-lg font-bold">
            {done === 'sent' ? '¡Marcado registrado!' : 'Marcado guardado sin señal'}
          </p>
          <p className="mt-1 text-sm">
            {done === 'sent'
              ? kind === 'ingreso'
                ? 'Tu ingreso quedó registrado con foto y ubicación.'
                : 'Tu salida quedó registrada con foto y ubicación.'
              : 'Se enviará solo cuando vuelva la señal. No cierres sesión.'}
          </p>
        </div>
        {aviso.avisar && (
          <div className="rounded-xl border border-amber-400 bg-amber-50 p-4 text-left">
            <p className="text-base font-bold text-amber-900">Salida antes de tiempo</p>
            <p className="mt-1 text-sm text-amber-900">
              Este día se contará como <strong>medio día de trabajo (0.5)</strong>. Tu
              coordinador lo verá en su pantalla: si tuviste permiso o una emergencia,
              coordínalo con él.
            </p>
          </div>
        )}
        <Link to="/movil" className="block rounded-xl bg-blue-600 py-4 font-bold text-white">
          Volver a mi turno
        </Link>
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-base font-medium text-gray-900">
          {assignmentId
            ? 'Ese turno ya no está disponible o no te corresponde.'
            : 'No sabemos qué turno estás marcando.'}
        </p>
        <p className="text-sm text-gray-600">
          Vuelve a "Mi turno" y usa el botón de la tarjeta del turno que quieres marcar.
        </p>
        <Link to="/movil" className="block rounded-xl bg-blue-600 py-4 font-bold text-white">
          Volver
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold text-gray-900">{titulo}</h1>
        <p className="text-sm text-gray-600">
          {shift.position.pdv.name} · {shift.position.start_time.slice(0, 5)} a{' '}
          {shift.position.end_time.slice(0, 5)}
        </p>
      </header>

      {!gpsReady && (
        <p className="rounded-lg bg-gray-100 p-3 text-sm text-gray-700">
          Obteniendo ubicación… <strong>no hace falta esperarla para tomar la foto ni para
          marcar.</strong> Si no llega, tu marca queda como «Sin GPS» y tu coordinador la revisa.
        </p>
      )}
      {gpsReady && gps && (
        <p className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900">
          Ubicación lista (precisión ±{Math.round(gps.accuracy)} m).
        </p>
      )}
      {gpsReady && !gps && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm text-amber-900">
            {gpsError ? gpsErrorMessage(gpsError) : 'No pudimos obtener tu ubicación.'}
          </p>
          <button
            type="button"
            onClick={() => void pedirUbicacion()}
            className="w-full rounded-lg bg-amber-600 px-4 py-3 text-sm font-semibold text-white"
          >
            Reintentar ubicación
          </button>
          <p className="text-xs text-amber-800">
            Si sigue sin funcionar puedes marcar igual: quedará registrado como "Sin GPS" y tu
            coordinador lo revisará.
          </p>
        </div>
      )}

      {puedeFijarUbicacion && gpsReady && gps && (
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          {ubicFijada ? (
            <p className="text-sm text-green-700">
              Ubicación de {shift.position.pdv.name} actualizada con tu posición actual.
            </p>
          ) : confirmarUbic ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-800">{textoConfirmacionUbicacion(distanciaGuardada)}</p>
              <p className="text-xs text-gray-600">
                Se guardará {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (precisión ±
                {Math.round(gps.accuracy)} m) para {shift.position.pdv.name}.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={guardandoUbic}
                  onClick={() => void fijarUbicacionPdv()}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {guardandoUbic ? 'Guardando…' : 'Sí, actualizar'}
                </button>
                <button
                  type="button"
                  disabled={guardandoUbic}
                  onClick={() => setConfirmarUbic(false)}
                  className="flex-1 rounded-lg bg-gray-200 px-4 py-3 text-sm font-semibold text-gray-800"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmarUbic(true)}
              className="text-sm text-blue-600 underline"
            >
              Fijar ubicación de esta tienda
            </button>
          )}
        </section>
      )}

      <section className="space-y-2">
        <label className="block text-sm font-medium text-gray-800">Foto obligatoria</label>
          {/*
            🔴 LA FOTO NO DEPENDE DEL GPS. DECISIÓN DEL USUARIO, 14-set:
            *«Toda foto tiene que ser libre de eso, en todos los formularios que tengamos»*.

            Acá decía `!gpsReady || busyPhoto`, y eso dejó a dos impulsadoras sin poder
            registrarse: un `<input type="file" disabled>` se dibuja y no hace nada al tocarlo,
            así que cuando el GPS no contestaba el botón quedaba muerto sin decir por qué.

            La espera no protegía ningún dato: marcar «Sin GPS» siempre fue válido, y las
            coordenadas de la marca **se leen al ENVIAR, no al fotografiar** —así que si la
            ubicación llega mientras la persona revisa la foto, la fila igual sale con
            coordenadas—. Lo único que lograba el candado era impedir trabajar.

            Queda solo `busyPhoto`, que NO es lo mismo: es «estoy comprimiendo la foto
            anterior», dura un instante y termina siempre.
          */}
        {/*
          🌐 EL INPUT NATIVO SE ESCONDE A PROPÓSITO. `<input type="file">` sin envolver
          muestra el botón y el texto que trae el NAVEGADOR del celular en SU idioma —"Choose
          file" / "No file chosen"— sin que el CSS ni ningún texto de la app puedan tocarlo.
          Con el teléfono en inglés (o Chrome en inglés aunque el sistema esté en español),
          la impulsadora ve la pantalla en inglés en el único paso que de verdad importa.

          El arreglo es el mismo patrón que ya usa `CampoFoto.tsx`: un `<label>` visible con
          el texto que nosotros elegimos, envolviendo un input real pero oculto (`sr-only`,
          NUNCA `display:none` — un input oculto así sigue enfocable por teclado y lector de
          pantalla, que `display:none` le quita). El click en el label activa el input como
          si se hubiera tocado directo.

          Sigue siendo SOLO CÁMARA (`capture="environment"`, sin botón de galería): la foto
          del marcado prueba que la persona estaba ahí a esa hora, y dejar elegir de galería
          abriría la puerta a marcar desde la casa con una foto de otro día — la razón que
          `CampoFoto.tsx` ya deja escrita para no reusarlo acá.
        */}
        <label
          htmlFor="foto-marcado"
          className={`flex min-h-[44px] w-full items-center justify-center rounded-lg border px-3 py-3 text-center text-sm font-medium ${
            busyPhoto
              ? 'cursor-default border-gray-300 bg-gray-100 text-gray-400'
              : 'cursor-pointer border-blue-300 bg-blue-50 text-blue-800'
          }`}
        >
          {photoUrl ? 'Cambiar foto' : 'Tomar foto'}
        </label>
        <input
          id="foto-marcado"
          type="file"
          accept="image/*"
          capture="environment"
          disabled={busyPhoto}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onPhotoPicked(file);
            e.target.value = '';
          }}
          className="sr-only"
        />
        {busyPhoto && <p className="text-sm text-gray-600">Procesando foto…</p>}
        {avisoFoto !== null && (
          <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
            {avisoFoto}
          </p>
        )}
        {photoUrl && (
          <img
            src={photoUrl}
            alt="Foto sellada del marcado"
            className="w-full rounded-lg border border-gray-200"
          />
        )}
      </section>

      {fields.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">
            Datos de la campaña
          </h2>
          {fields.map((f) => (
            <div key={f.id} className="space-y-1">
              <label htmlFor={`campo-${f.id}`} className="block text-sm font-medium text-gray-800">
                {f.label}
                {f.required && <span className="text-red-600"> *</span>}
              </label>

              {f.kind === 'texto' && (
                <input
                  id={`campo-${f.id}`}
                  type="text"
                  value={values[f.id] ?? ''}
                  onChange={(e) => setValues((p) => ({ ...p, [f.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 p-3 text-base"
                />
              )}

              {f.kind === 'numero' && (
                <input
                  id={`campo-${f.id}`}
                  type="number"
                  inputMode="decimal"
                  value={values[f.id] ?? ''}
                  onChange={(e) => setValues((p) => ({ ...p, [f.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 p-3 text-base"
                />
              )}

              {f.kind === 'seleccion' && (
                <select
                  id={`campo-${f.id}`}
                  value={values[f.id] ?? ''}
                  onChange={(e) => setValues((p) => ({ ...p, [f.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 p-3 text-base"
                >
                  <option value="">Selecciona…</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}

              {/* Foto de una PREGUNTA del formulario: acepta cámara o galería (0043). No es la
                  foto del marcado —esa está arriba y se queda solo con la cámara—, porque acá la
                  imagen es un dato del formulario y no la prueba de que la persona estaba en la
                  tienda a esa hora. */}
              {f.kind === 'foto' && (
                <CampoFoto
                  nombre={f.label}
                  listaTexto="Foto lista para enviar."
                  lista={fieldPhotos[f.id] !== undefined}
                  onElegir={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void onFieldPhotoPicked(f, file);
                    e.target.value = '';
                  }}
                />
              )}
            </div>
          ))}
        </section>
      )}

      {aviso.avisar && (
        <div className="rounded-lg border-2 border-amber-500 bg-amber-50 p-3">
          <p className="text-base font-bold text-amber-900">Estás saliendo antes de tiempo</p>
          <p className="mt-1 text-sm text-amber-900">{aviso.mensaje}</p>
        </div>
      )}

      {error && (
        <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </p>
      )}

      {/* Qué falta, DICHO, y no solo un botón apagado. Un botón gris sin explicación es la
          peor versión de este bloqueo: la persona no sabe qué le piden y llama a su
          coordinador. El texto nombra el campo que falta. */}
      {faltaObligatorio !== null && photo && !saving && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {faltaObligatorio} Es obligatorio para poder registrar.
        </p>
      )}

      <button
        onClick={onRegistrar}
        // El `disabled` solo depende de datos que la pantalla YA tiene en memoria: la foto y la
        // lista de campos que se cargó al abrir. Nunca de una lectura que pueda fallar — si los
        // campos no se pudieron leer, `fields` queda vacío, no hay obligatorios y se marca
        // igual. Ninguna lectura tiene permiso para esconderle el botón de marcar a nadie.
        disabled={!photo || saving || busyPhoto || faltaObligatorio !== null}
        className="w-full rounded-xl bg-blue-600 py-5 text-lg font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
      >
        {saving ? 'Registrando…' : 'Registrar'}
      </button>

      <Link to="/movil" className="block py-2 text-center text-sm text-gray-600 underline">
        Cancelar
      </Link>

      {/* Confirmación de la salida temprana: informa, no impide. Se muestra pegada al
          borde inferior porque es donde llega el pulgar en un celular de 375 px. */}
      {confirmarSalida && aviso.avisar && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-salida-temprana"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
            <p id="titulo-salida-temprana" className="text-lg font-bold text-amber-900">
              Estás saliendo antes de tiempo
            </p>
            <p className="mt-2 text-sm text-gray-800">{aviso.mensaje}</p>
            <p className="mt-2 text-sm text-gray-600">
              Si tienes permiso, cita médica o una emergencia, márcala igual y avisa a tu
              coordinador para que lo registre.
            </p>
            <div className="mt-4 space-y-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setConfirmarSalida(false);
                  void onSubmit();
                }}
                className="w-full rounded-xl bg-amber-600 py-4 text-base font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                Registrar salida igual
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => setConfirmarSalida(false)}
                className="w-full rounded-xl bg-gray-200 py-4 text-base font-bold text-gray-800"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
